import React, { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MaterialSymbol from '../MaterialSymbol';
import PayseraTicketsLogo from './PayseraTicketsLogo';
import HeaderActions from './HeaderActions';
import LocaleSelectors from './LocaleSelectors';
import AppearanceSelector from './AppearanceSelector';
import MobileMenu from './MobileMenu';
import MobileSearchMode from './MobileSearchMode';
import ConnectedOperatorBanner from '../ConnectedOperatorBanner';
import useHeaderProps from './useHeaderProps';
import { headerTranslationsShape } from './propTypes';
import { useHeaderOverlayContext } from './HeaderOverlayContext';
import {
    MOBILE_ICON_BUTTON_SIZE, HEADER_HEIGHT_XS, HEADER_HEIGHT_MD, HEADER_HEIGHT_CSS_VARIABLE,
} from './constants';
import { HOVERED_ICON_BUTTON_SX, PRESSED_ICON_BUTTON_BGCOLOR } from '../highContrastSx';
import { CONTENT_MAX_WIDTH } from '../../config/layout';

const APPBAR_SX = {
    bgcolor: 'background.default',
    borderBottom: '1px solid',
    borderColor: 'divider',
};

const TOOLBAR_SX = {
    minHeight: { xs: HEADER_HEIGHT_XS, md: HEADER_HEIGHT_MD },
    height: { xs: HEADER_HEIGHT_XS, md: HEADER_HEIGHT_MD },
    width: '100%',
    maxWidth: CONTENT_MAX_WIDTH,
    mx: 'auto',
    px: 2,
    position: 'relative',
};

const PREFERENCE_CONTROLS_SX = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.25,
};

const MOBILE_HAMBURGER_ICON_SIZE = 24;
const MOBILE_CLOSE_ICON_SIZE = 20;

const MOBILE_ICON_BUTTON_BASE_SX = {
    borderRadius: 2,
    width: MOBILE_ICON_BUTTON_SIZE,
    height: MOBILE_ICON_BUTTON_SIZE,
    transition: 'all 0.15s',
    '&:hover': HOVERED_ICON_BUTTON_SX,
    '&:active': { bgcolor: PRESSED_ICON_BUTTON_BGCOLOR },
};

const HAMBURGER_SLOT_SX = {
    display: { xs: 'flex', lg: 'none' },
    alignItems: 'center',
    width: MOBILE_ICON_BUTTON_SIZE,
};

const buildHamburgerButtonSx = isOpen => ({
    ...MOBILE_ICON_BUTTON_BASE_SX,
    color: isOpen ? 'primary.main' : 'grey.700',
});

const CHECKOUT_HEADER_SPACER_SX = {
    height: { xs: HEADER_HEIGHT_XS, md: HEADER_HEIGHT_MD },
};

const IN_FLOW_LOGO_SLOT_SX = {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
};

const CENTERED_BELOW_DESKTOP_LOGO_SLOT_SX = {
    ...IN_FLOW_LOGO_SLOT_SX,
    position: { xs: 'absolute', lg: 'static' },
    left: { xs: '50%', lg: 'auto' },
    transform: { xs: 'translateX(-50%)', lg: 'none' },
};

const SPACER_SLOT_SX = {
    display: { xs: 'none', lg: 'block' },
    flex: 1,
};

const DESKTOP_ACTIONS_SLOT_SX = {
    display: { xs: 'none', lg: 'flex' },
    alignItems: 'center',
    gap: 1,
    flexShrink: 0,
};

const CHECKOUT_ALL_BREAKPOINT_LOCALE_SLOT_SX = {
    display: 'flex',
    alignItems: 'center',
    ml: 'auto',
    flexShrink: 0,
};

const MOBILE_SEARCH_SLOT_SX = {
    display: { xs: 'flex', lg: 'none' },
    alignItems: 'center',
    justifyContent: 'flex-end',
    width: MOBILE_ICON_BUTTON_SIZE,
    ml: 'auto',
};

const MOBILE_SEARCH_BUTTON_SX = {
    ...MOBILE_ICON_BUTTON_BASE_SX,
    color: 'grey.600',
};

const BACKDROP_SX = {
    display: { xs: 'block', lg: 'none' },
    position: 'fixed',
    inset: 0,
    bgcolor: 'rgba(0, 0, 0, 0.3)',
    zIndex: theme => theme.zIndex.appBar - 1,
};

function Header({
    mobileSearchEnabled = false,
    checkoutMode = false,
    translations,
}) {
    const headerProps = useHeaderProps();
    const overlay = useHeaderOverlayContext();
    const appBarRef = useRef(null);
    const [appBarHeight, setAppBarHeight] = useState(null);

    useLayoutEffect(() => {
        if (!checkoutMode) {
            setAppBarHeight(null);
            return undefined;
        }
        const node = appBarRef.current;
        if (!node) {
            return undefined;
        }
        const supportsResizeObserver = typeof ResizeObserver !== 'undefined';
        if (!supportsResizeObserver) {
            return undefined;
        }
        const measure = () => setAppBarHeight(node.offsetHeight);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(node);
        return () => observer.disconnect();
    }, [checkoutMode]);

    useLayoutEffect(() => {
        if (appBarHeight == null) {
            return undefined;
        }
        const rootStyle = document.documentElement.style;
        rootStyle.setProperty(HEADER_HEIGHT_CSS_VARIABLE, `${appBarHeight}px`);
        return () => rootStyle.removeProperty(HEADER_HEIGHT_CSS_VARIABLE);
    }, [appBarHeight]);

    const showMobileSearchMode = !checkoutMode && mobileSearchEnabled && overlay.isSearchOpen;
    const renderBackdrop = !checkoutMode && (overlay.isMenuOpen || showMobileSearchMode);
    const hamburgerLabel = overlay.isMenuOpen ? translations.closeMenu : translations.openMenu;

    const hamburgerButton = (
        <IconButton
            aria-label={hamburgerLabel}
            aria-expanded={overlay.isMenuOpen}
            onClick={overlay.toggleMenu}
            sx={buildHamburgerButtonSx(overlay.isMenuOpen)}
            data-testid="redesign-header-hamburger"
        >
            <MaterialSymbol
                name={overlay.isMenuOpen ? 'close' : 'menu'}
                size={overlay.isMenuOpen ? MOBILE_CLOSE_ICON_SIZE : MOBILE_HAMBURGER_ICON_SIZE}
            />
        </IconButton>
    );

    const operatorBanner = <ConnectedOperatorBanner translations={translations} checkoutMode={checkoutMode} />;

    const localeSelectors = (
        <Box sx={PREFERENCE_CONTROLS_SX} data-testid="header-preference-controls">
            <LocaleSelectors
                locale={headerProps.locale}
                country={headerProps.country}
                availableLocales={headerProps.availableLocales}
                availableCountries={headerProps.availableCountries}
                countryLabel={translations.country}
                languageLabel={translations.language}
                countryHidden={checkoutMode}
            />
            <AppearanceSelector />
        </Box>
    );

    return (
        <>
            <AppBar
                ref={appBarRef}
                position={checkoutMode ? 'fixed' : 'sticky'}
                color="inherit"
                elevation={0}
                sx={APPBAR_SX}
                data-testid="redesign-header"
            >
                {checkoutMode ? operatorBanner : null}
                <Toolbar disableGutters sx={TOOLBAR_SX}>
                    {showMobileSearchMode ? (
                        <MobileSearchMode
                            title={translations.searchTitle}
                            closeLabel={translations.closeSearch}
                            onClose={overlay.close}
                        />
                    ) : (
                        <>
                            {checkoutMode ? null : (
                                <Box sx={HAMBURGER_SLOT_SX}>
                                    {hamburgerButton}
                                </Box>
                            )}

                            <Box sx={checkoutMode ? IN_FLOW_LOGO_SLOT_SX : CENTERED_BELOW_DESKTOP_LOGO_SLOT_SX}>
                                <PayseraTicketsLogo
                                    href={headerProps.mainPageUrl}
                                    title={translations.logoAlt}
                                />
                            </Box>

                            {checkoutMode ? (
                                <Box sx={CHECKOUT_ALL_BREAKPOINT_LOCALE_SLOT_SX}>
                                    {localeSelectors}
                                </Box>
                            ) : (
                                <>
                                    <Box sx={SPACER_SLOT_SX} aria-hidden="true" />

                                    <Box sx={DESKTOP_ACTIONS_SLOT_SX}>
                                        <HeaderActions
                                            account={headerProps.account}
                                            myTicketsUrl={headerProps.buyerSpaUrl}
                                            manageEventsUrl={headerProps.adminSpaUrl}
                                            translations={translations}
                                            localeSelectors={localeSelectors}
                                        />
                                    </Box>

                                    {mobileSearchEnabled ? (
                                        <Box sx={MOBILE_SEARCH_SLOT_SX}>
                                            <IconButton
                                                aria-label={translations.openSearch}
                                                onClick={overlay.openSearch}
                                                sx={MOBILE_SEARCH_BUTTON_SX}
                                                data-testid="redesign-header-search-mobile"
                                            >
                                                <MaterialSymbol name="search" size={20} />
                                            </IconButton>
                                        </Box>
                                    ) : null}
                                </>
                            )}
                        </>
                    )}
                </Toolbar>

                {checkoutMode ? null : (
                    <MobileMenu
                        open={overlay.isMenuOpen}
                        headerProps={headerProps}
                        translations={translations}
                        onNavigate={overlay.close}
                    />
                )}

                {showMobileSearchMode ? (
                    <Box
                        sx={{ display: { xs: 'block', lg: 'none' } }}
                        data-testid="redesign-mobile-search-panel-slot"
                    />
                ) : null}
            </AppBar>

            {checkoutMode ? (
                <Box
                    sx={appBarHeight != null ? { height: appBarHeight } : CHECKOUT_HEADER_SPACER_SX}
                    aria-hidden="true"
                    data-testid="redesign-header-spacer"
                />
            ) : null}

            {renderBackdrop ? (
                <Box
                    aria-hidden="true"
                    onClick={overlay.close}
                    sx={BACKDROP_SX}
                    data-testid="redesign-mobile-menu-backdrop"
                />
            ) : null}
        </>
    );
}

Header.propTypes = {
    mobileSearchEnabled: PropTypes.bool,
    checkoutMode: PropTypes.bool,
    translations: headerTranslationsShape.isRequired,
};

export default Header;
